import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
  CreditCard, Shield, Info, Loader2, Check, X, 
  Building, Users, Briefcase, ChevronRight, Star
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subscribe = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  
  // Check if returning from Stripe
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchTiers();
  }, []);

  useEffect(() => {
    if (sessionId) {
      navigate(`/subscription-success?session_id=${sessionId}`);
    }
  }, [sessionId, navigate]);

  const fetchTiers = async () => {
    try {
      const response = await fetch(`${API}/payments/platform-tiers`);
      if (response.ok) {
        const data = await response.json();
        setTiers(data.tiers);
      }
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
      toast.error('Failed to load subscription options');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    // Check if user is logged in
    if (!user || !user.email) {
      toast.error('Please log in to subscribe');
      navigate('/login');
      return;
    }

    if (!selectedTier) {
      toast.error('Please select a subscription tier');
      return;
    }

    setProcessing(true);

    try {
      const response = await fetch(`${API}/payments/subscribe`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          tier_id: selectedTier,
          user_email: user.email,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();

      if (response.ok && data.checkout_url) {
        toast.success('Redirecting to secure checkout...');
        // Use window.location.replace for more reliable redirect
        window.location.replace(data.checkout_url);
      } else {
        toast.error(data.detail || 'Failed to create checkout session');
        setProcessing(false);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to initiate subscription. Please try again.');
      setProcessing(false);
    }
  };

  const getTierIcon = (tierId) => {
    switch (tierId) {
      case 'basic': return <Briefcase className="w-6 h-6" />;
      case 'professional': return <Users className="w-6 h-6" />;
      case 'enterprise': return <Building className="w-6 h-6" />;
      default: return <Star className="w-6 h-6" />;
    }
  };

  const getTierColor = (tierId) => {
    switch (tierId) {
      case 'basic': return 'border-blue-500/50 hover:border-blue-500';
      case 'professional': return 'border-[#b8860b]/50 hover:border-[#b8860b]';
      case 'enterprise': return 'border-purple-500/50 hover:border-purple-500';
      default: return 'border-gray-600';
    }
  };

  const getTierBadgeColor = (tierId) => {
    switch (tierId) {
      case 'basic': return 'bg-blue-500/20 text-blue-400';
      case 'professional': return 'bg-[#b8860b]/20 text-[#b8860b]';
      case 'enterprise': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#b8860b]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] to-[#1a2d4a] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Platform Access</h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Choose your subscription tier to access Bethereum&apos;s private credit tools and workflows.
            BETH credits are allocated monthly for platform access.
          </p>
          {user && (
            <p className="text-sm text-[#b8860b] mt-2">
              Logged in as: {user.email}
            </p>
          )}
        </div>

        {/* Compliance Notice */}
        <Card className="bg-[#1a2d4a]/50 border-blue-500/30 mb-8 max-w-3xl mx-auto">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-blue-300 font-medium mb-1">BETH Platform Credits</p>
                <p className="text-gray-400">
                  BETH is a <strong>non-transferable platform access credit</strong> used exclusively to access software tools and services. 
                  BETH is NOT an investment, security, or currency. No refunds. No resale. No trading. 
                  Credits are debited monthly for platform access.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tier Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {tiers.map((tier) => (
            <Card 
              key={tier.id}
              onClick={() => setSelectedTier(tier.id)}
              className={`bg-[#0d1f3c] cursor-pointer transition-all duration-300 ${getTierColor(tier.id)} ${
                selectedTier === tier.id 
                  ? 'ring-2 ring-offset-2 ring-offset-[#0a1628] ' + (tier.id === 'professional' ? 'ring-[#b8860b]' : tier.id === 'enterprise' ? 'ring-purple-500' : 'ring-blue-500')
                  : ''
              }`}
            >
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className={`p-2 rounded-lg ${getTierBadgeColor(tier.id)}`}>
                    {getTierIcon(tier.id)}
                  </div>
                  {tier.id === 'professional' && (
                    <span className="px-2 py-1 text-xs bg-[#b8860b] text-white rounded-full">
                      Popular
                    </span>
                  )}
                </div>
                <CardTitle className="text-white text-xl">{tier.name}</CardTitle>
                <CardDescription className="text-gray-400">{tier.ideal_for}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">${tier.monthly_cost_usd.toLocaleString()}</span>
                    <span className="text-gray-400">/month</span>
                  </div>
                  <p className="text-sm text-[#b8860b] mt-1">
                    {tier.beth_credits.toLocaleString()} BETH credits
                  </p>
                </div>

                <div className="space-y-3 mb-6">
                  {tier.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-300">{feature}</span>
                    </div>
                  ))}
                  {tier.limitations.map((limitation, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-500">{limitation}</span>
                    </div>
                  ))}
                </div>

                <Button
                  className={`w-full ${
                    selectedTier === tier.id 
                      ? 'bg-[#b8860b] hover:bg-[#9a7209]' 
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTier(tier.id);
                  }}
                >
                  {selectedTier === tier.id ? 'Selected' : 'Select Plan'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Subscribe Button */}
        {selectedTier && (
          <div className="max-w-md mx-auto">
            <Card className="bg-[#0d1f3c] border-[#b8860b]/30">
              <CardContent className="py-6">
                <div className="text-center mb-4">
                  <p className="text-gray-400 text-sm">Selected Plan</p>
                  <p className="text-white font-bold text-xl">
                    {tiers.find(t => t.id === selectedTier)?.name} - ${tiers.find(t => t.id === selectedTier)?.monthly_cost_usd.toLocaleString()}/month
                  </p>
                </div>
                
                <Button
                  onClick={handleSubscribe}
                  disabled={processing}
                  className="w-full bg-[#b8860b] hover:bg-[#9a7209] text-white font-bold py-3"
                  data-testid="subscribe-button"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Subscribe Now
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                {!user && (
                  <p className="text-center text-yellow-500 text-sm mt-3">
                    Please <button onClick={() => navigate('/login')} className="underline">log in</button> to subscribe
                  </p>
                )}

                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                  <Shield className="w-3 h-3" />
                  Secured by Stripe
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* How it Works */}
        <div className="mt-12 max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-white text-center mb-6">How Platform Access Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: '1', title: 'Choose Tier', desc: 'Select the plan that fits your needs' },
              { step: '2', title: 'Pay Monthly', desc: 'Secure payment via Stripe' },
              { step: '3', title: 'Get Credits', desc: 'BETH credits allocated to your account' },
              { step: '4', title: 'Access Tools', desc: 'Use platform features with your credits' }
            ].map((item, idx) => (
              <div key={idx} className="text-center p-4">
                <div className="w-10 h-10 rounded-full bg-[#b8860b]/20 text-[#b8860b] font-bold flex items-center justify-center mx-auto mb-3">
                  {item.step}
                </div>
                <h3 className="text-white font-medium mb-1">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Important Notice */}
        <Card className="bg-[#1a2d4a]/30 border-gray-700 mt-8 max-w-3xl mx-auto">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-400">
                <p className="font-medium text-gray-300 mb-2">Important Information</p>
                <ul className="space-y-1 text-xs">
                  <li>• BETH credits are for platform access only - they cannot be traded, sold, or transferred</li>
                  <li>• Subscriptions renew monthly - cancel anytime</li>
                  <li>• No crypto wallet required - credits are managed internally</li>
                  <li>• Enterprise clients may request custom arrangements</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Subscribe;
